import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { chooseProtectedScreen } from "../lib/access-state";
import { AccessDenied } from "../pages/AccessDenied";
import { NotDashboardAccount } from "../pages/NotDashboardAccount";
import { NoAccess } from "./NoAccess";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Entry needs a staff account AND at least one granted tab. Non-staff get a
 * clear "not a dashboard account" screen; staff with zero grants get the
 * empty state; a broken access check gets an outage screen with a Retry, and
 * never a redirect to the login form. Render-only: the server guards are the
 * real wall.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAdmin, allowedTabs, authStatus, permissionsStatus, refreshSession } = useAuth();
  const location = useLocation();
  const [retrying, setRetrying] = useState(false);

  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      await refreshSession();
    } finally {
      setRetrying(false);
    }
  }, [refreshSession]);

  const screen = chooseProtectedScreen({
    authStatus,
    permissionsStatus,
    isAdmin,
    allowedTabs,
  });

  if (screen === "loading") {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (screen === "outage") {
    return (
      <div className="flex min-h-svh w-full flex-col justify-center bg-background p-6">
        <NoAccess kind="check-failed" onRetry={() => void retry()} retrying={retrying} />
      </div>
    );
  }

  if (screen === "login") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (screen === "not-dashboard-account") {
    return <NotDashboardAccount />;
  }

  if (screen === "access-denied") {
    return <AccessDenied />;
  }

  return <>{children}</>;
};
